import { useMemo } from 'react';
import { Button } from '@southfields-digital/mxmz-react-ui';
import { validate as isValidUUID } from 'uuid';

import { transformSportDataValue } from 'src/components/GraphicPropertiesForm';
import { MatchStatsResponse } from 'src/types/statsperform/matchStats';
import { getCards, getMatchStatsFromStat2, createStatRows } from './utils';

import SportDataInput from '../SportDataInput';
import StatRow from './StatRow';

import styles from './MatchStats.module.scss';

type Props = {
  fixtureData: MatchStatsResponse;
  fixtureId: string;
  handleImportData: (value: string | PropertyValue[]) => void;
  selectedProperty?: Properties;
  onFixtureIdChange: (value: string) => void;
};

export default function MatchStatsContent({
  fixtureData,
  fixtureId,
  handleImportData,
  selectedProperty,
  onFixtureIdChange,
}: Props) {
  const {
    matchInfo,
    liveData: { lineUp, card },
  } = fixtureData;

  const [homeTeam, awayTeam] = matchInfo.contestant;
  const [homeLineUp, awayLineUp] = lineUp || [];

  const [yellowHome, redHome] = getCards(card, homeTeam.id);
  const [yellowAway, redAway] = getCards(card, awayTeam.id);

  const homeStats = getMatchStatsFromStat2(homeLineUp?.stat);
  const awayStats = getMatchStatsFromStat2(awayLineUp?.stat);

  const isRepeater = selectedProperty?.type === 'repeater';

  const items = useMemo(
    () =>
      createStatRows({
        homeStats,
        awayStats,
        yellowHome,
        redHome,
        yellowAway,
        redAway,
      }),
    [homeStats, awayStats, yellowHome, redHome, yellowAway, redAway]
  );

  const handleSingleImport = !isRepeater ? handleImportData : undefined;

  const propertyValues = useMemo(
    () =>
      items.map<PropertyValue>((item) => ({
        IdentRowHome: item.homeValue,
        IdentRowCenter: item.label,
        IdentRowAway: item.awayValue,
      })),
    [items]
  );

  const transformedPropertyValues = useMemo(() => {
    if (!isRepeater || !selectedProperty || !propertyValues.length) return propertyValues;
    return isValidUUID(selectedProperty.id)
      ? (transformSportDataValue(propertyValues, selectedProperty) as PropertyValue[])
      : propertyValues;
  }, [propertyValues, selectedProperty, isRepeater]);

  const handleRepeaterImport = () => handleImportData(transformedPropertyValues);

  const rowChildren = isRepeater && (
    <Button onClick={handleRepeaterImport} variant="tertiary">
      Import
    </Button>
  );

  return (
    <div className={styles.Container}>
      <SportDataInput fixtureId={fixtureId} onChange={onFixtureIdChange} />
      <div className={styles.Content}>
        <StatRow
          label=""
          homeValue={homeTeam.officialName}
          awayValue={awayTeam.officialName}
          onClickImport={handleSingleImport}
        >
          {rowChildren}
        </StatRow>
        {items.map((item) => (
          <StatRow
            key={item.label}
            label={item.label}
            homeValue={item.homeValue}
            awayValue={item.awayValue}
            onClickImport={handleSingleImport}
          />
        ))}
      </div>
    </div>
  );
}
